import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { User, Lock, Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!username || !password) return toast.error("Kullanıcı adı ve şifre gerekli");
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Hoş geldiniz, ${u.name}`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0B1220] lg:flex">
      {/* LEFT — heritage brand panel */}
      <div className="relative hidden overflow-hidden lg:flex lg:w-[52%] flex-col justify-between bg-[#0B1220] px-14 py-12">
        {/* subtle texture / vignette */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #C8A96A 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#C8A96A]/10 blur-[120px]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/40 to-transparent" />

        {/* crest / brand */}
        <div className="relative flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#C8A96A]/50 bg-[#C8A96A]/5">
            <span className="font-serif-lux text-xl font-700 text-[#C8A96A]" style={{ fontWeight: 700 }}>Z</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif-lux text-xl tracking-wide text-white" style={{ fontWeight: 600 }}>ZOR</div>
            <div className="text-[10px] font-medium uppercase tracking-[0.35em] text-[#C8A96A]">New Generation Academy</div>
          </div>
        </div>

        {/* headline */}
        <div className="relative">
          <div className="mb-6 h-px w-16 bg-[#C8A96A]/60" />
          <h1 className="font-serif-lux text-5xl leading-[1.1] text-white" style={{ fontWeight: 500 }}>
            Eğitimde<br />
            <span className="italic text-[#C8A96A]">seçkin</span> bir<br />
            takip deneyimi.
          </h1>
          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-slate-400">
            1–7. sınıflar için haftalık plan, ders ve performans takibi.
            Yönetim, koç, öğrenci ve veli — tek ve zarif bir panelde.
          </p>
        </div>

        {/* footer meta */}
        <div className="relative flex items-center justify-between border-t border-white/10 pt-6">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-[#C8A96A]" />
            Kurumsal güvenlik · Şifreli oturum
          </div>
          <div className="text-xs font-medium uppercase tracking-[0.25em] text-slate-500">Est. 2026 · London Standard</div>
        </div>
      </div>

      {/* RIGHT — refined form */}
      <div className="flex flex-1 items-center justify-center bg-[#F6F4EF] px-6 py-14 sm:px-12">
        <div className="w-full max-w-sm animate-pop-in">
          {/* mobile brand */}
          <div className="mb-10 flex items-center gap-3 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#C8A96A] bg-white">
              <span className="font-serif-lux text-lg font-bold text-[#0B1220]">Z</span>
            </div>
            <div className="font-serif-lux text-lg font-semibold text-[#0B1220]">ZOR Academy</div>
          </div>

          <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#B08D4F]">Üye Girişi</p>
          <h2 className="mt-3 font-serif-lux text-4xl text-[#0B1220]" style={{ fontWeight: 500 }}>Tekrar hoş geldiniz</h2>
          <p className="mt-3 text-sm text-slate-500">Devam etmek için hesap bilgilerinizi giriniz.</p>

          <form onSubmit={submit} className="mt-9 space-y-5">
            <div>
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Kullanıcı Adı</label>
              <div className="group relative">
                <User className="absolute left-0 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="kullanıcı adınız"
                  className="w-full border-0 border-b border-slate-300 bg-transparent py-2.5 pl-7 pr-2 text-[15px] text-[#0B1220] outline-none transition-colors placeholder:text-slate-400 focus:border-[#B08D4F]"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-password-input"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full border-0 border-b border-slate-300 bg-transparent py-2.5 pl-7 pr-9 text-[15px] text-[#0B1220] outline-none transition-colors placeholder:text-slate-400 focus:border-[#B08D4F]"
                />
                <button type="button" onClick={() => setShow((s) => !s)} data-testid="toggle-password-btn" className="absolute right-0 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-[#0B1220]">
                  {show ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              disabled={loading}
              className="group mt-2 flex w-full items-center justify-center gap-2 rounded-none bg-[#0B1220] py-4 text-sm font-semibold uppercase tracking-[0.18em] text-white transition-all hover:bg-[#161F33] active:scale-[0.99] disabled:opacity-60"
            >
              {loading ? "Giriş yapılıyor" : "Giriş Yap"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </form>

          <p className="mt-10 text-center text-xs text-slate-400">© 2026 ZOR New Generation Academy</p>
        </div>
      </div>
    </div>
  );
}
